import { basketSections } from "../Session/plan-lesson/ai-lesson-planning/recommendationsApi"
import type {
  RecommendationApiBasketItem,
  RecommendationApiData,
  RecommendationApiEngineInfo,
  RecommendationApiHistory,
  RecommendationApiLearner,
} from "../Session/plan-lesson/ai-lesson-planning/aiLessonPlanningTypes"

export interface BasketMeta {
  color: string
  bg: string
  fg: string
  help: string
}

export const BASKET_META: Record<string, BasketMeta> = {
  progress: {
    color: "#3B5BCC",
    bg: "#E4EAFB",
    fg: "#2E4AA6",
    help: "Next skills to move forward",
  },
  preference: {
    color: "#C2416B",
    bg: "#FBE4EA",
    fg: "#9C2A51",
    help: "Formats and topics this student uses often",
  },
  relearn: {
    color: "#D98A1A",
    bg: "#FDEEDB",
    fg: "#8A5300",
    help: "Skipped or older skills to revisit",
  },
}

const FALLBACK_META: BasketMeta = {
  color: "#8C93A3",
  bg: "#EEF0F5",
  fg: "#4A5163",
  help: "",
}

export function basketMeta(key: string): BasketMeta {
  return BASKET_META[key] || FALLBACK_META
}

const HEAT_STEPS = ["#F1F3F8", "#D4DBEF", "#A9B7E0", "#6E82C6", "#3B5BCC"]

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
]

export interface ReportItem {
  key: string
  rank: number
  title: string
  strand: string
  typeLabel: string
  sourceId: string
  reasons: string[]
  levels: Array<{ text: string; far: boolean }>
  score: string | null
  scorePct: number
}

export interface ReportBasket {
  key: string
  label: string
  meta: BasketMeta
  count: number
  pct: number
  items: ReportItem[]
}

export interface ReportMatrixRow {
  strand: string
  total: number
  cells: Array<{ basket: string; n: number; step: number }>
}

export interface ReportLevelBar {
  year: number
  n: number
  isYearUsed: boolean
  far: boolean
  heightPct: number
}

export interface ReportReasonBar {
  label: string
  n: number
  widthPct: number
}

export interface StudentReportModel {
  profileYear: number | null
  inferredYear: number | null
  yearUsed: number | null
  country: string | null
  externalId: string | null
  learnerId: string | null
  isNew: boolean
  asOf: string
  executedAt: string
  totalCount: number
  basketLine: string
  baskets: ReportBasket[]
  historyContents: number
  historyUsed: number
  historyExcluded: number
  afterAsOf: number
  lastDate: string
  daysAgo: string
  warnings: string[]
  notes: string[]
  rulesNote: string
  matrix: ReportMatrixRow[]
  levelBars: ReportLevelBar[]
  levelSummary: string
  reasonBars: ReportReasonBar[]
  typeLine: string
  placementText: string
  engine: string
  lang: string
  poolSize: string
  builtAt: string
  precomputeMs: string
  ms: string
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null
}

export function formatReportDate(value?: string | null): string {
  if (!value) return "—"
  const datePart = value.slice(0, 10).split("-")
  if (datePart.length !== 3) return value
  const [y, m, d] = datePart.map(Number)
  if (!y || !m || !d) return value
  const dateText = `${d} ${MONTHS[m - 1]} ${y}`
  if (value.length <= 10) return dateText
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return dateText
  const hh = String(parsed.getHours()).padStart(2, "0")
  const mm = String(parsed.getMinutes()).padStart(2, "0")
  return `${parsed.getDate()} ${MONTHS[parsed.getMonth()]} ${parsed.getFullYear()}, ${hh}:${mm}`
}

function dayDiff(from?: string | null, to?: string | null): number | null {
  if (!from || !to) return null
  const a = Date.parse(from.slice(0, 10))
  const b = Date.parse(to.slice(0, 10))
  if (Number.isNaN(a) || Number.isNaN(b)) return null
  return Math.round((a - b) / 86400000)
}

export function contentTypeLabel(item: RecommendationApiBasketItem): string {
  const raw = (item.type || item.contentType || "").trim().toLowerCase()
  if (raw === "online" || raw === "quiz" || raw === "online activity") {
    return "Online Activity"
  }
  if (raw === "worksheet" || raw === "document" || raw === "pdf") return "PDF"
  return item.type || item.contentType || "Activity"
}

function pluralType(label: string, n: number): string {
  if (label === "PDF") return n === 1 ? "PDF" : "PDFs"
  const lower = label.toLowerCase()
  if (n === 1) return lower
  return lower.endsWith("y") ? `${lower.slice(0, -1)}ies` : `${lower}s`
}

function itemLevels(item: RecommendationApiBasketItem): number[] {
  const levels = Array.isArray(item.levels)
    ? item.levels.filter(l => typeof l === "number" && Number.isFinite(l))
    : []
  if (levels.length > 0) return levels
  const year = num(item.year)
  return year === null ? [] : [year]
}

export function buildStudentReport(
  data: RecommendationApiData,
  firstName: string
): StudentReportModel {
  const learner: RecommendationApiLearner = data.learner || {}
  const profileYear = num(learner.profileYear)
  const inferredYear = num(data.inferredYear)
  const yearUsed = num(data.yearUsed) ?? inferredYear ?? profileYear

  const sections = basketSections(data).map(section => ({
    ...section,
    items: section.items.filter(item => item?.sourceId && item?.title),
  }))
  const itemCount = sections.reduce((sum, s) => sum + s.items.length, 0)
  const totalCount = num(data.totalCount) ?? itemCount
  const pctBase = totalCount > 0 ? totalCount : itemCount

  const allItems = sections.flatMap(section =>
    section.items.map(item => ({ basket: section.reason, item }))
  )

  const rawScores = allItems
    .map(({ item }) => num(item.score))
    .filter((s): s is number => s !== null)
  const scoreScale = Math.max(1, ...rawScores)

  const baskets: ReportBasket[] = sections.map(section => {
    const count = section.items.length
    return {
      key: section.reason,
      label: section.label,
      meta: basketMeta(section.reason),
      count,
      pct: pctBase > 0 ? Math.round((count / pctBase) * 100) : 0,
      items: section.items.map((item, idx) => {
        const score = num(item.score)
        return {
          key: `${section.reason}:${item.sourceId}:${idx}`,
          rank: idx + 1,
          title: item.title,
          strand: item.strand || item.domain || "Other",
          typeLabel: contentTypeLabel(item),
          sourceId: item.sourceId,
          reasons: Array.isArray(item.reasons) ? item.reasons.filter(Boolean) : [],
          levels: itemLevels(item).map(level => {
            if (yearUsed === null) return { text: `Y${level}`, far: false }
            const d = level - yearUsed
            const suffix = d === 0 ? "" : ` (${d > 0 ? "+" : "−"}${Math.abs(d)})`
            return { text: `Y${level}${suffix}`, far: d >= 2 }
          }),
          score: score === null ? null : score.toFixed(2),
          scorePct: score === null ? 0 : Math.round((score / scoreScale) * 100),
        }
      }),
    }
  })

  const basketKeys = baskets.map(b => b.key)

  const strands: Record<string, Record<string, number>> = {}
  allItems.forEach(({ basket, item }) => {
    const strand = item.strand || item.domain || "Other"
    if (!strands[strand]) strands[strand] = { total: 0 }
    strands[strand][basket] = (strands[strand][basket] || 0) + 1
    strands[strand].total += 1
  })
  let maxCell = 1
  Object.values(strands).forEach(row => {
    basketKeys.forEach(k => {
      maxCell = Math.max(maxCell, row[k] || 0)
    })
  })
  const matrix: ReportMatrixRow[] = Object.keys(strands)
    .sort((a, b) => strands[b].total - strands[a].total)
    .map(strand => ({
      strand,
      total: strands[strand].total,
      cells: basketKeys.map(basket => {
        const n = strands[strand][basket] || 0
        const step = n === 0 ? 0 : Math.max(1, Math.round((n / maxCell) * 4))
        return { basket, n, step }
      }),
    }))

  const levelCounts: Record<number, number> = {}
  let below = 0
  let at = 0
  let above = 0
  let far = 0
  allItems.forEach(({ item }) => {
    const levels = itemLevels(item)
    if (levels.length === 0) return
    levels.forEach(l => {
      levelCounts[l] = (levelCounts[l] || 0) + 1
    })
    if (yearUsed === null) return
    const top = Math.max(...levels)
    const low = Math.min(...levels)
    if (low <= yearUsed && top >= yearUsed) at += 1
    else if (top < yearUsed) below += 1
    else above += 1
    if (low - yearUsed >= 2) far += 1
  })
  const levelKeys = Object.keys(levelCounts).map(Number)
  if (yearUsed !== null) levelKeys.push(yearUsed)
  const levelBars: ReportLevelBar[] = []
  if (levelKeys.length > 0) {
    const lo = Math.min(...levelKeys)
    const hi = Math.max(...levelKeys)
    let lvMax = 1
    for (let y = lo; y <= hi; y++) lvMax = Math.max(lvMax, levelCounts[y] || 0)
    for (let y = lo; y <= hi; y++) {
      const n = levelCounts[y] || 0
      levelBars.push({
        year: y,
        n,
        isYearUsed: y === yearUsed,
        far: yearUsed !== null && y - yearUsed >= 2,
        heightPct: (n / lvMax) * 100,
      })
    }
  }
  const levelSummary =
    yearUsed === null
      ? ""
      : `${below} below · ${at} at · ${above} above${far ? ` (${far} 2+ above)` : ""}`

  const reasonCounts: Record<string, number> = {}
  allItems.forEach(({ item }) => {
    ;(item.reasons || []).filter(Boolean).forEach(r => {
      reasonCounts[r] = (reasonCounts[r] || 0) + 1
    })
  })
  const reasonKeys = Object.keys(reasonCounts)
    .sort((a, b) => reasonCounts[b] - reasonCounts[a])
    .slice(0, 6)
  const reasonMax = reasonKeys.length ? reasonCounts[reasonKeys[0]] : 1
  const reasonBars: ReportReasonBar[] = reasonKeys.map(label => ({
    label,
    n: reasonCounts[label],
    widthPct: (reasonCounts[label] / reasonMax) * 100,
  }))

  const typeCounts: Record<string, number> = {}
  allItems.forEach(({ item }) => {
    const label = contentTypeLabel(item)
    typeCounts[label] = (typeCounts[label] || 0) + 1
  })
  const typeLine = Object.keys(typeCounts)
    .map(t => `${typeCounts[t]} ${pluralType(t, typeCounts[t])}`)
    .join(" · ")

  const name = firstName || "this student"
  let placementText = ""
  if (inferredYear !== null && profileYear !== null) {
    if (inferredYear > profileYear) {
      placementText = `From recent work the engine places ${name} at Year ${inferredYear}, ahead of the profile year. `
    } else if (inferredYear < profileYear) {
      placementText = `From recent work the engine places ${name} at Year ${inferredYear}, behind the profile year. Expect more Relearn picks. `
    } else {
      placementText = "The engine estimate matches the profile year. "
    }
  }
  if (yearUsed !== null) {
    placementText += `Recommendations are built for Year ${yearUsed}.`
  }

  const history: RecommendationApiHistory = data.history || {}
  const contents = num(history.contents) ?? 0
  const outOfPool = num(history.outOfPool) ?? 0
  const unmapped = num(history.unmapped) ?? 0
  const afterAsOf = num(history.afterAsOf) ?? 0
  const used = Math.max(0, contents - outOfPool - unmapped - afterAsOf)
  const days = dayDiff(data.asOf || data.executedAt, history.lastDate)

  const engineInfo: RecommendationApiEngineInfo = data.engineInfo || {}
  const fmtNumber = (v: unknown) => {
    const n = num(v)
    return n === null ? "—" : n.toLocaleString("en-GB")
  }

  return {
    profileYear,
    inferredYear,
    yearUsed,
    country: learner.country || null,
    externalId: learner.externalId || null,
    learnerId: learner.id || null,
    isNew: Boolean(data.isNew),
    asOf: formatReportDate(data.asOf),
    executedAt: formatReportDate(data.executedAt),
    totalCount,
    basketLine: baskets.map(b => `${b.count} ${b.label.toLowerCase()}`).join(" · "),
    baskets,
    historyContents: contents,
    historyUsed: used,
    historyExcluded: contents - used,
    afterAsOf,
    lastDate: formatReportDate(history.lastDate),
    daysAgo:
      days === null
        ? "—"
        : days <= 0
        ? "Today"
        : days === 1
        ? "1 day ago"
        : `${days} days ago`,
    warnings: Array.isArray(data.warnings) ? data.warnings.filter(Boolean) : [],
    notes: Array.isArray(data.notes) ? data.notes.filter(Boolean) : [],
    rulesNote: data.rulesNote || "",
    matrix,
    levelBars,
    levelSummary,
    reasonBars,
    typeLine,
    placementText,
    engine: data.engine || "—",
    lang: data.lang || "—",
    poolSize: fmtNumber(engineInfo.poolSize),
    builtAt: formatReportDate(engineInfo.builtAt),
    precomputeMs: fmtNumber(engineInfo.precomputeMs),
    ms: fmtNumber(engineInfo.ms),
  }
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  const first = parts[0][0] || ""
  const last = parts.length > 1 ? parts[parts.length - 1][0] || "" : ""
  return (first + last).toUpperCase()
}

export function heatStyle(step: number): { background: string; color: string } {
  return {
    background: HEAT_STEPS[step] || HEAT_STEPS[0],
    color: step >= 3 ? "#FFFFFF" : "#1C2333",
  }
}
